/**
 * Utilidades para el manejo seguro de fechas
 *
 * Este módulo proporciona funciones para formatear fechas evitando
 * el bug clásico de JavaScript relacionado con conversión de timezone.
 */

/**
 * Formatea una fecha evitando conversión de timezone.
 *
 * Maneja dos tipos de entrada:
 * - Fecha tipo "date": YYYY-MM-DD
 * - Timestamp completo: YYYY-MM-DDTHH:mm:ss.sssZ o YYYY-MM-DD HH:mm:ss
 *
 * IMPORTANTE: new Date("2025-10-16") interpreta la fecha como UTC medianoche,
 * lo que en México (UTC-6) resulta en el día anterior (15/10/2025). Este método
 * extrae la parte de fecha y parsea manualmente para garantizar correctitud.
 *
 * @param {string} dateString - Fecha en formato ISO YYYY-MM-DD o timestamp completo
 * @param {string} locale - Locale para formatear (default: 'es-MX')
 * @param {object} options - Opciones de formato para toLocaleDateString
 * @returns {string} Fecha formateada o '-' si es inválida
 *
 * @example
 * formatDateSafe('2025-10-16') // '16/10/2025' (en lugar de '15/10/2025')
 * formatDateSafe('2025-10-11T14:30:00.000Z') // '11/10/2025'
 * formatDateSafe('2025-10-16', 'es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) // '16 de octubre de 2025'
 */
export const formatDateSafe = (
  dateString,
  locale = 'es-MX',
  options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }
) => {
  if (!dateString) return '-';

  try {
    // Extraer solo la parte de fecha (YYYY-MM-DD) si es un timestamp
    // "2025-10-11T14:30:00.000Z" → "2025-10-11"
    // "2025-10-11 14:30:00" → "2025-10-11"
    // "2025-10-11" → "2025-10-11"
    const dateOnly = dateString.split('T')[0].split(' ')[0];

    // Parse manual para evitar conversión de timezone
    const [year, month, day] = dateOnly.split('-').map(Number);

    // Validar que los valores son números válidos
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      console.warn(`Invalid date string: ${dateString}`);
      return '-';
    }

    const date = new Date(year, month - 1, day);

    // Validar que la fecha es válida
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date created from: ${dateString}`);
      return '-';
    }

    return date.toLocaleDateString(locale, options);
  } catch (error) {
    console.error(`Error formatting date ${dateString}:`, error);
    return '-';
  }
};

/**
 * Formatea un rango de fechas de forma segura.
 *
 * @param {string} startDate - Fecha inicial en formato YYYY-MM-DD
 * @param {string} endDate - Fecha final en formato YYYY-MM-DD
 * @param {string} separator - Separador entre fechas (default: ' - ')
 * @returns {string} Rango formateado: "DD/MM/YYYY - DD/MM/YYYY"
 *
 * @example
 * formatDateRange('2025-10-16', '2025-10-31') // '16/10/2025 - 31/10/2025'
 */
export const formatDateRange = (startDate, endDate, separator = ' - ') => {
  const formattedStart = formatDateSafe(startDate);
  const formattedEnd = formatDateSafe(endDate);

  if (formattedStart === '-' && formattedEnd === '-') return '-';
  if (formattedStart === '-') return formattedEnd;
  if (formattedEnd === '-') return formattedStart;

  return `${formattedStart}${separator}${formattedEnd}`;
};

/**
 * Formatea una fecha con formato largo (ej: "16 de octubre de 2025")
 *
 * @param {string} dateString - Fecha en formato YYYY-MM-DD
 * @returns {string} Fecha en formato largo
 *
 * @example
 * formatDateLong('2025-10-16') // '16 de octubre de 2025'
 */
export const formatDateLong = (dateString) => {
  return formatDateSafe(dateString, 'es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Formatea una fecha con formato corto (ej: "16 oct. 2025")
 *
 * @param {string} dateString - Fecha en formato YYYY-MM-DD
 * @returns {string} Fecha en formato corto
 *
 * @example
 * formatDateShort('2025-10-16') // '16 oct. 2025'
 */
export const formatDateShort = (dateString) => {
  return formatDateSafe(dateString, 'es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Convierte una fecha ISO string a objeto Date de forma segura.
 *
 * @param {string} dateString - Fecha en formato YYYY-MM-DD
 * @returns {Date|null} Objeto Date o null si es inválido
 *
 * @example
 * const date = parseDateSafe('2025-10-16');
 * date.getDate() // 16 (en lugar de 15)
 */
export const parseDateSafe = (dateString) => {
  if (!dateString) return null;

  try {
    const [year, month, day] = dateString.split('-').map(Number);

    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null;
    }

    const date = new Date(year, month - 1, day);

    if (isNaN(date.getTime())) {
      return null;
    }

    return date;
  } catch (error) {
    console.error(`Error parsing date ${dateString}:`, error);
    return null;
  }
};

// Export default para importar todo el módulo
export default {
  formatDateSafe,
  formatDateRange,
  formatDateLong,
  formatDateShort,
  parseDateSafe
};
